import { Ellipsis } from "lucide-react";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Modal } from "../../components/ui/modal";
import { cn } from "../../handlers/cn.utils";
import useIsLoggedIn from "../../hooks/use-IsLoggedIn";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import {
	decodeClaimToken,
	hasValidClaimSession,
	setActiveManagedCompanyId,
} from "../../services/companies/claimSession";
import {
	useActivelyHiringCompanies,
	useCompany,
	useCompanyJobs,
	useFeaturedEmployers,
} from "../../services/create-resume/queries";
import Header from "../Profile/components/Header";
import GenerateMultiJobImage from "../Social/GenerateMultiJobImage";
import CompanyJobs from "./components/CompanyJobs";
import CompanyProfileSummaryCard from "./components/CompanyProfileSummaryCard";
import Overview from "./components/Overview";
import SimilarCompanies from "./components/SimilarCompanies";

function Company() {
	const isLoggedIn = useIsLoggedIn();
	const { id } = useParams();
	const navigate = useNavigate();

	const [employers, setEmployers] = React.useState([]);

	const { data: response, isLoading, refetch } = useFeaturedEmployers();
	const { data, isLoading: loadingCompany, isError } = useCompany(id);
	const { data: jobs, isLoading: isLoadingJobs } = useCompanyJobs(id);
	const { data: companies, isLoading: isLoadingActivelyHiringCompanies } = useActivelyHiringCompanies();

	const [companyData, setCompanyData] = React.useState();
	const [companyJobs, setCompanyJobs] = React.useState([]);
	const [activelyHiringCompanies, setActivelyHiringCompanies] = React.useState([]);

	const isCompanyActivelyHiring = (companyId) => {
		return activelyHiringCompanies.includes(companyId);
	};

	useEffect(() => {
		if (!isLoadingActivelyHiringCompanies && companies) {
			setActivelyHiringCompanies(companies.data);
		}
	}, [data]);

	useEffect(() => {
		if (!loadingCompany && data) {
			setCompanyData(data.data);
		}
	}, [data]);

	useEffect(() => {
		if (!loadingCompany && !isError && data?.data?.isClaimed) {
			navigate(`/companies/${id}/verified`, { replace: true });
		}
	}, [data, id, isError, loadingCompany, navigate]);

	useEffect(() => {
		if (!isLoadingJobs && jobs) {
			setCompanyJobs(jobs.data);
			if (!(jobs.data.length > 0)) {
				setActiveTab("about");
			}
		}
	}, [jobs]);

	useEffect(() => {
		if (!isLoading && response) {
			// A non-array body (an error string, for instance) still has .slice, so it
			// reached the render and threw on .map. Only ever hold an array here.
			setEmployers(Array.isArray(response.data) ? response.data : []);
		}
	}, [isLoading, response]);

	const showPostGenerator = useFeatureFlag("posterGenerator");
	const showCompanyClaim = useFeatureFlag("companyClaim");
	const claimSession = decodeClaimToken();
	const hasAdminClaimAccess = hasValidClaimSession() && claimSession?.role === "admin";

	const [activeTab, setActiveTab] = React.useState("jobs");
	const [isModalOpen, setIsModalOpen] = React.useState(false);

	const handleMenuClick = () => {
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const handleCompanyAccessClick = React.useCallback(
		(event) => {
			if (!hasAdminClaimAccess || !companyData?._id) {
				return;
			}

			event.preventDefault();
			setActiveManagedCompanyId(companyData._id);
			navigate("/company/dashboard");
		},
		[companyData?._id, hasAdminClaimAccess, navigate],
	);

	useEffect(() => {
		refetch();
	}, [id]);

	if (!loadingCompany && !isError && data?.data?.isClaimed) {
		return null;
	}

	return (
		<main>
			<Header isLoggedIn={isLoggedIn} />

			<div className="relative overflow-hidden">
				<div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<nav className="flex" aria-label="Breadcrumb">
						<ol className="inline-flex items-center">
							<li className="inline-flex items-center">
								<Link
									className="inline-flex items-center text-base font-medium text-gray-500 hover:text-gray-700"
									to="/"
								>
									Home
								</Link>
							</li>
							<li className="inline-flex items-center">
								<span className="mx-2 text-gray-400">/</span>
								<Link
									className="inline-flex items-center text-base font-medium text-gray-500 hover:text-gray-700"
									to="/companies"
								>
									Companies
								</Link>
							</li>
							<li className="inline-flex items-center">
								<span className="mx-2 text-gray-400">/</span>
								<Link
									className="inline-flex items-center text-base font-medium text-gray-700 hover:text-gray-900"
									to={`/companies/${companyData?._id}`}
								>
									{companyData?.company_name}
								</Link>
							</li>
						</ol>
					</nav>
				</div>
			</div>

			{loadingCompany && !isError ? (
				<section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="py-8 animate-pulse">
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center">
								<div className="w-24 h-24 rounded-lg overflow-hidden shrink-0 border border-gray-100 bg-gray-200" />
								<div className="ml-6">
									<div className="h-6 bg-gray-200 rounded w-1/2 my-1" />
									<div className="h-4 bg-gray-200 rounded w-3/4 my-1" />
								</div>
							</div>
							<div className="btn h-10 bg-gray-200 rounded w-20" />
						</div>
						<div className="flex flex-wrap gap-2 text-sm text-gray-600 font-inter mb-4">
							<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100/80 text-gray-800">
								<div className="w-20 h-5 bg-gray-200 rounded" />
							</span>
							<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100/80 text-gray-800">
								<div className="w-20 h-5 bg-gray-200 rounded" />
							</span>
							<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-light text-success-default">
								<div className="w-20 h-5 bg-gray-200 rounded" />
							</span>
						</div>
						<div className="flex flex-wrap gap-4 text-md text-gray-600 font-inter">
							<div className="flex items-center">
								<span className="font-semibold mr-1">
									<div className="w-20 h-5 bg-gray-200 rounded" />
								</span>
								<a href="#" target="_blank" rel="noopener noreferrer" className="text-primary-700 font-medium">
									<div className="w-20 h-5 bg-gray-200 rounded" />
								</a>
							</div>
						</div>
					</div>
				</section>
			) : (
				<section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					<Helmet>
						<title>{companyData?.company_name + " | Dproz" || "Dproz"}</title>
						<meta name="description" content={companyData?.about_us} />
						<meta property="keywords" content={companyData?.company_name} />
						<meta property="og:image" content={companyData?.logo_url} />
						<meta property="og:image:secure_url" content={companyData?.logo_url} />
						<meta property="og:image:alt" content={companyData?.company_name + " logo"} />
						<meta property="og:image:type" content="image/png" />
						<meta property="og:image:width" content="96" />
						<meta property="og:image:height" content="96" />
						<meta property="og:description" content={companyData?.about_us} />
						<meta property="og:title" content={companyData?.company_name} />
						<meta property="og:type" content="website" />
					</Helmet>

					{showCompanyClaim && (
						<div className="relative mb-5 mt-3 w-full overflow-hidden rounded-lg bg-primary-50 p-4 text-foreground">
							<div className="absolute right-0 top-0 h-40 w-40 -translate-y-1/2 translate-x-1/2 rounded-full bg-primary-100" />
							<div className="absolute bottom-0 left-0 h-32 w-32 translate-y-1/2 -translate-x-1/2 rounded-full bg-primary-100" />
							<div className="relative flex min-h-[70px] flex-col gap-4 md:flex-row md:items-center md:justify-between">
								<div>
									<h2 className="text-lg font-bold text-foreground">Is This Your Company?</h2>
									{companyData?.isClaimed ? (
										<p className="max-w-2xl text-sm text-muted-foreground">
											Login in by verifying your work email to manage it.
										</p>
									) : (
										<p className="max-w-2xl text-sm text-muted-foreground">
											We created this page using publicly available information. Verify your work email to manage it.
										</p>
									)}
								</div>
								<Link
									to={hasAdminClaimAccess ? "/company/dashboard" : `/companies/${companyData?._id}/claim`}
									onClick={handleCompanyAccessClick}
									className="shrink-0"
								>
									<Button
										variant="secondary"
										className="border border-primary-200 bg-white text-primary-700 hover:bg-primary-100"
									>
										{hasAdminClaimAccess ? "Manage Company" : "Learn More"}
									</Button>
								</Link>
							</div>
						</div>
					)}

					<div className="flex justify-between gap-4">
						<div className="flex-1 py-8">
							<CompanyProfileSummaryCard
								company={companyData}
								isActivelyHiring={isCompanyActivelyHiring(companyData?._id)}
								cardPadding={false}
							/>
						</div>
						{showPostGenerator && (
							<Button size="sm" variant="outline" onClick={handleMenuClick}>
								<Ellipsis className="w-4 h-4 rotate-90" />
							</Button>
						)}
					</div>
					<div className="flex flex-col md:flex-row gap-8">
						<div className="flex-1">
							<div className="">
								<div className="border-b border-gray-200">
									<nav className="-mb-px flex space-x-8" aria-label="Tabs">
										<span
											className={cn(
												"cursor-pointer py-4 px-1 font-semibold text-md text-gray-500 hover:text-gray-700 hover:border-gray-500 border-b-2 border-transparent",
												{
													"border-gray-900 text-gray-900": activeTab === "jobs",
												},
											)}
											onClick={() => setActiveTab("jobs")}
										>
											Jobs
										</span>
										<span
											className={cn(
												"cursor-pointer py-4 px-1 font-semibold text-md text-gray-500 hover:text-gray-700 hover:border-gray-500 border-b-2 border-transparent",
												{
													"border-gray-900 text-gray-900": activeTab === "about",
												},
											)}
											onClick={() => setActiveTab("about")}
										>
											About
										</span>
									</nav>
								</div>
								{activeTab === "jobs" ? (
									<>
										{companyJobs ? (
											<CompanyJobs jobs={companyJobs} logo={companyData?.logo_url} />
										) : (
											<div className="pt-6">
												<h2 className="text-2xl font-semibold mb-4 font-plus-jakarta">No jobs available</h2>
											</div>
										)}
									</>
								) : (
									<div className="mt-4">
										<div className="pt-6">
											<h2 className="text-2xl font-semibold mb-4 font-plus-jakarta">
												About {companyData?.company_name}
											</h2>
											<p className="text-gray-700 mb-4">{companyData?.about_us}</p>
											{false && (
												<>
													<h3 className="text-lg font-semibold mb-2">Mission</h3>
													<p className="text-gray-700 mb-4">
														To empower businesses and individuals through cutting-edge technology solutions.
													</p>
												</>
											)}
											{false && (
												<>
													<h3 className="text-lg font-semibold mb-2">Values</h3>
													<ul className="list-disc list-inside mb-4">
														<li className="text-gray-700">Innovation</li>
														<li className="text-gray-700">Integrity</li>
														<li className="text-gray-700">Collaboration</li>
														<li className="text-gray-700">Excellence</li>
													</ul>
												</>
											)}
										</div>
									</div>
								)}
							</div>
						</div>
						{companyData && <Overview companyData={companyData} />}
					</div>
				</section>
			)}

			{!loadingCompany && (
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					<h2 className="text-2xl font-semibold mb-6">Similar Companies</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{isLoading ? (
							<>
								{[...new Array(3)].map((_, index) => (
									<div className="bg-gray-50 rounded-lg p-6 flex flex-col h-80 animate-pulse" key={index}>
										<div className="flex items-center mb-3">
											<div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 border border-gray-100 bg-gray-200" />
											<div className="ml-4 h-6 bg-gray-200 rounded w-1/2" />
										</div>
										<div className="h-4 bg-gray-200 rounded w-full my-1" />
										<div className="flex flex-wrap gap-2 mb-3">
											<span className="inline-flex items-center h-6 aspect-square px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700" />
											<span className="inline-flex items-center h-6 aspect-square px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700" />
											<span className="inline-flex items-center h-6 aspect-square px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700" />
										</div>
										<div className="mt-auto pt-4">
											<div className="flex space-x-2">
												<span className="btn bg-gray-200 text-black w-full" />
												<button className="btn text-black border-gray-200 w-full h-10" />
											</div>
										</div>
									</div>
								))}
							</>
						) : (
							<>
								{employers.slice(2, 5).map((company, index) => (
									<SimilarCompanies key={index} company={company} />
								))}
							</>
						)}
					</div>
				</div>
			)}

			<Modal isOpen={isModalOpen} onClose={handleCloseModal} title="Generate Image">
				<GenerateMultiJobImage
					companyName={companyData?.company_name}
					companyLink={`www.dproz.com/companies/${id}`}
					jobs={companyJobs}
					logoUrl={companyData?.logo_url}
				/>
			</Modal>
		</main>
	);
}

export default Company;
